/**
 * OptimizedThemeContext.tsx
 * 
 * An optimized version of the ThemeContext with:
 * 1. Improved initialization to avoid React context creation timing issues
 * 2. Performance optimizations for smoother user experience
 * 3. Limited effects scope to prevent potential hanging issues
 * 4. Proper cleanup to prevent memory leaks
 */
import * as React from 'react';

// Import from centralized types
import { Theme } from '../types/Theme';
import { THEME_CONSTANTS } from '../constants';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isDark: boolean;
}

// Default context value
const defaultThemeContext: ThemeContextType = {
  theme: 'light', // Fixed to light theme as auto-detection is disabled
  setTheme: () => {},
  isDark: false
};

// Create a factory function to defer context creation
// This ensures React is fully initialized before context creation
const createThemeContext = () => {
  const context = React.createContext<ThemeContextType>(defaultThemeContext);
  context.displayName = 'OptimizedThemeContext';
  return context;
};

// Export the context using a getter function to ensure lazy initialization
// This prevents the error with accessing React.createContext too early
export const useThemeContext = () => {
  // We'll initialize the actual context in the ThemeProvider component
  // This guarantees React is fully loaded when the context is created
  const contextRef = React.useRef<React.Context<ThemeContextType> | null>(null);
  
  if (!contextRef.current) {
    contextRef.current = createThemeContext();
  }
  
  return contextRef.current;
};

// Create a properly initialized context that won't be evaluated until the component renders
// This prevents the "Cannot read properties of undefined (reading 'createContext')" error
let ThemeContext: React.Context<ThemeContextType>;

// Lazy initialization function that creates the context only when needed
const getThemeContext = (): React.Context<ThemeContextType> => {
  if (!ThemeContext) {
    ThemeContext = createThemeContext();
  }
  return ThemeContext;
};

export function OptimizedThemeProvider({ 
  children,
  defaultTheme = 'light' // Changed from 'system' as auto-detection is disabled
}: { 
  children: React.ReactNode;
  defaultTheme?: Theme;
}) {
  // Initialize state with a simple function to avoid expensive operations during render
  const [theme, setThemeState] = React.useState<Theme>(() => {
    // Light/dark theme toggle will be a Plus feature in the future
    // For now, we force light theme for all users
    return THEME_CONSTANTS.DEFAULT_THEME as Theme;
    
    /* Temporarily disabled until light/dark mode becomes a Plus feature
    // Only access localStorage in a browser environment and handle potential errors
    if (typeof window !== 'undefined') {
      try {
        const savedTheme = localStorage.getItem('theme');
        return (savedTheme as Theme) || defaultTheme;
      } catch (error) {
        // Silent localStorage error in production
      }
    }
    return defaultTheme;
    */
  });
  
  const [isDark, setIsDark] = React.useState(false);

  // Memoized setTheme function to avoid unnecessary re-renders
  const setTheme = React.useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    // We handle localStorage and DOM updates in the effect, not here
  }, []);

  // Apply theme changes with cleanup
  React.useEffect(() => {
    // Safely check for browser environment
    if (typeof window === 'undefined') return;
    
    // Disabled automatic theme detection - will be a Plus feature
    // Simply set isDark based on the selected theme
    setIsDark(theme === 'dark');
    
    // Update DOM based on the selected theme only
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    /* Temporarily disabled until light/dark mode becomes a Plus feature
    // Save preference
    try {
      localStorage.setItem(THEME_CONSTANTS.STORAGE_KEY, theme);
    } catch (error) {
      // Silent localStorage error in production
    }

    // System theme detection disabled - will be enabled for Plus users in the future
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleMediaChange = () => {
      if (theme === 'system') {
        setIsDark(mediaQuery.matches);
        if (mediaQuery.matches) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    };
    
    mediaQuery.addEventListener('change', handleMediaChange);
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
    */
  }, [theme]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = React.useMemo<ThemeContextType>(() => {
    return { theme, setTheme, isDark };
  }, [theme, setTheme, isDark]);

  // Get the context instance just-in-time when the provider renders
  // This ensures React is fully loaded before creating the context
  const ContextInstance = getThemeContext();
  
  return (
    <ContextInstance.Provider value={contextValue}>
      {children}
    </ContextInstance.Provider>
  );
}

// Custom hook with improved error handling
export function useOptimizedTheme() {
  // Get the context instance just-in-time when the hook is called
  // This ensures React is fully loaded before accessing the context
  const ContextInstance = getThemeContext();
  const context = React.useContext(ContextInstance);
  
  if (context === undefined) {
    throw new Error('useOptimizedTheme must be used within an OptimizedThemeProvider');
  }
  
  return context;
}
